import { useCallback, useEffect, useMemo } from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { FormAutocomplete } from '@/components/ui/custom/Autocomplete/Autocomplete';
import { Skeleton } from '@/components/ui/skeleton';
import useDialogAlert from '@/hooks/useDialogAlert';
import useIntents from '@/hooks/useIntents';
import { selectIntentByNodeId } from '@/redux/dialogs/selectors';
import { updateNode } from '@/redux/nodes/actions';
import { selectBrainId } from '@/redux/session/selectors';
import { RootState } from '@/shared/types/state';
import { isRequired } from '@/util/validation/constants';

type Props = { nodeId: string };

const Intent = ({ nodeId }: Props) => {
  const brainId = useSelector(selectBrainId);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { cleanIntents, listStatus } = useIntents(brainId);
  const intent = useSelector(
    (state: RootState) => selectIntentByNodeId(state, nodeId),
    shallowEqual
  );

  const intentErrorMessage = useMemo(() => {
    if (!intent) {
      return t('dialog.intent_required');
    }
    if (cleanIntents?.some((i) => i.intent === intent)) {
      return undefined;
    }
    return t('dialog.intent_not_found');
  }, [intent, cleanIntents, t]);

  const { setFieldError } = useDialogAlert({
    id: nodeId,
    nodeId,
    title: t('dialog.intent'),
    type: 'intent',
  });

  useEffect(() => {
    if (listStatus !== 'success') {
      return;
    }
    setFieldError('intent', intentErrorMessage);
  }, [intentErrorMessage, listStatus, setFieldError]);

  const defaultIntent = useMemo(() => intent ?? '', [intent]);

  const {
    trigger,
    control,
    getValues,
    resetField,
    formState: { errors },
  } = useForm<{ intent: string }>({ mode: 'onChange', defaultValues: { intent: defaultIntent } });

  useEffect(() => {
    resetField('intent', { defaultValue: defaultIntent });
  }, [defaultIntent, resetField]);

  const onChange = useCallback(async () => {
    const isValid = await trigger();
    if (isValid) {
      const selectedIntent = getValues('intent');
      if (selectedIntent) {
        dispatch(updateNode({ nodeId, node: { intent: selectedIntent } }));
      }
    }
  }, [dispatch, getValues, nodeId, trigger]);

  const options = cleanIntents?.map((i) => ({ value: i.intent, label: `#${i.intent}` })) ?? [];

  if (listStatus === 'pending') {
    return <Skeleton className="h-8 w-25" />;
  }
  return (
    <FormAutocomplete
      control={control}
      name="intent"
      options={options}
      onChange={onChange}
      placeholder={t('intent.select_an_intent')}
      rules={isRequired}
      error={Boolean(errors.intent)}
      errorMessage={errors.intent?.message}
    />
  );
};

export default Intent;
